<script setup lang="ts">
import { primeVueTwMerger } from '@/components/primevue'
import { useLocalization } from '@/plugins/localization'
import { useAuthStore } from '@/stores/auth.store'
import { useNavbarStore } from '@/stores/navbar.store'
import { isUuid, trimString, uuidShort } from '@/utils'
import type { MenuItem } from 'primevue/menuitem'
import { computed, ref } from 'vue'
import { useRoute, useRouter, type RouteLocation } from 'vue-router'

const route = useRoute()
const router = useRouter()
const { t } = useLocalization()
const navbarStore = useNavbarStore()
const authStore = useAuthStore()

const home = ref({
  icon: 'pi pi-home',
  route: { name: 'home' },
})

function getPathLabel(path: string, matchedRoute: RouteLocation) {
  if (isUuid(path)) {
    const uuidName = navbarStore.nameOfId[path]
    if (uuidName == null) {
      return uuidShort(path)
    } else if (uuidName.length > 30) {
      return trimString(uuidName, 30)
    } else {
      return uuidName
    }
  }
  if (/^[0-9]+$/.test(path)) {
    return path
  }
  return t('sidebar.' + matchedRoute.meta.labelKey)
}

interface NavbarItem extends MenuItem {
  label: string
  route: RouteLocation
  last: boolean
}

const items = computed<NavbarItem[]>(() => {
  const pathArray = route.path.split('/').filter((x) => x)
  const paths: NavbarItem[] = []
  let fullPath = ''
  for (const path of pathArray) {
    fullPath += '/' + path
    const match = router.resolve(fullPath)
    paths.push({
      label: getPathLabel(path, match),
      route: match,
      last: false,
    })
  }

  // This is to avoid having 2 items on the entity edit if there is not a details page
  const last = paths[paths.length - 1]
  const secondToLast = paths[paths.length - 2]
  if (last && secondToLast && last.route.name === secondToLast.route.name) {
    const newLabel = last.label + ': ' + secondToLast.label
    paths.pop()
    paths[paths.length - 1].label = newLabel
  }

  if (paths.length > 0) {
    paths[paths.length - 1].last = true
  }

  return paths
})

const isSandbox = computed(() => /sandbox|local/i.test(authStore.tenantKey?.toLowerCase() || ''))
</script>

<template>
  <div class="flex items-center justify-between">
    <Breadcrumb
      :home="home"
      :model="items"
      class="min-h-12 overflow-x-auto py-4 pl-1"
      :pt="{ root: 'bg-transparent' }"
    >
      <template #item="{ item, props }">
        <router-link v-if="!item.last" v-slot="{ href, navigate }" :to="item.route" custom>
          <a :href="href" v-bind="props.action" class="hover:underline" @click="navigate">
            <span :class="[item.icon, { 'text-primary': items.length === 0 }, 'font-medium']" />
            <span class="text-nowrap font-medium">
              {{ item.label }}
            </span>
          </a>
        </router-link>
        <span v-else class="font-medium text-primary">
          {{ item.label }}
        </span>
      </template>
    </Breadcrumb>

    <Tag
      v-if="isSandbox"
      :pt="{ root: 'bg-emerald-200 font-semibold text-emerald-700' }"
      :pt-options="{ mergeProps: primeVueTwMerger }"
    >
      {{ authStore.tenantKey }}
    </Tag>
  </div>
</template>
